import { useGameSocket } from './useGameSocket'
import type { Player } from '~/types/game'
import type { CheckStateResult, CreateGameReferral, HostClaimErrorCode, HostClaimResult, JoinGameAck } from '~/types/socket'
import { bindSocketListener } from '~/utils/socketListeners'
import { emitWithAck, waitForSocket } from '~/utils/socketRequest'
import { setItemWithTTL, getItemWithTTL } from '~/utils/storageWithTTL'

const PER_GAME_SESSION_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

type PendingSessionTransfer = {
    sourceGameId: string
    targetGameId: string
    playerId: string
    name: string
    avatar: string | null
    startedAt: number
}

type StoredPlayerProfile = {
    id: string | null
    name: string | null
    avatar: string | null
}

export const useGameSession = () => {
    const { socket, connect, isConnected } = useGameSocket()
    const { clearReferralContext } = useReferralAttribution()

    // Identity State
    const localPlayerId = useState<string | null>('localPlayerId', () => null)
    const localGameId = useState<string | null>('localGameId', () => null)
    const isHost = useState<boolean>('isHost', () => false)
    const isHostAuthenticated = useState<boolean>('isHostAuthenticated', () => false)
    const hostSecrets = useState<Record<string, string>>('hostSecrets', () => ({}))
    const isClaimingHost = useState<boolean>('isClaimingHost', () => false)
    const hostClaimAttempts = useState<number>('hostClaimAttempts', () => 0)
    const hostClaimErrorCode = useState<HostClaimErrorCode | null>('hostClaimErrorCode', () => null)
    const hostClaimErrorMessage = useState<string | null>('hostClaimErrorMessage', () => null)
    const hostClaimLastAttemptAt = useState<number | null>('hostClaimLastAttemptAt', () => null)

    // Helper: Generate ID
    const generateId = () => Math.random().toString(36).substring(2, 15)

    const normalizeGameId = (gameId: string) => String(gameId || '').toUpperCase()
    const getCheckStatePayload = (gameId: string, updatedAt: number) => ({
        gameId: normalizeGameId(gameId),
        updatedAt: Number(updatedAt) || 0,
    })
    const hostSecretStorageKey = (gameId: string) => `host_secret_${normalizeGameId(gameId)}`

    const playerStorageKey = (gameId: string, field: 'id' | 'name' | 'avatar') =>
        `player_${field}_${normalizeGameId(gameId)}`
    const latestPlayerProfileStorageKey = 'player_profile_latest'
    const pendingSessionTransferStorageKey = 'pending_session_transfer'

    const clearStoredPlayerProfile = (gameId: string) => {
        if (import.meta.server) return
        const normalizedGameId = normalizeGameId(gameId)
        localStorage.removeItem(playerStorageKey(normalizedGameId, 'id'))
        localStorage.removeItem(playerStorageKey(normalizedGameId, 'name'))
        localStorage.removeItem(playerStorageKey(normalizedGameId, 'avatar'))
    }

    const persistLatestPlayerProfile = (profile: Pick<StoredPlayerProfile, 'name' | 'avatar'>) => {
        if (import.meta.server) return

        const normalizedName = profile.name?.trim() || null
        if (!normalizedName && !profile.avatar) return

        localStorage.setItem(latestPlayerProfileStorageKey, JSON.stringify({
            name: normalizedName,
            avatar: profile.avatar || null,
        }))
    }

    const persistStoredPlayerProfile = (gameId: string, profile: StoredPlayerProfile) => {
        if (import.meta.server) return

        const normalizedGameId = normalizeGameId(gameId)
        const normalizedName = profile.name?.trim() || null

        if (profile.id) {
            setItemWithTTL(playerStorageKey(normalizedGameId, 'id'), profile.id, PER_GAME_SESSION_TTL_MS)
        } else {
            localStorage.removeItem(playerStorageKey(normalizedGameId, 'id'))
        }

        if (normalizedName) {
            setItemWithTTL(playerStorageKey(normalizedGameId, 'name'), normalizedName, PER_GAME_SESSION_TTL_MS)
        } else {
            localStorage.removeItem(playerStorageKey(normalizedGameId, 'name'))
        }

        if (profile.avatar) {
            setItemWithTTL(playerStorageKey(normalizedGameId, 'avatar'), profile.avatar, PER_GAME_SESSION_TTL_MS)
        } else {
            localStorage.removeItem(playerStorageKey(normalizedGameId, 'avatar'))
        }

        persistLatestPlayerProfile({
            name: normalizedName,
            avatar: profile.avatar || null,
        })
    }

    const storeHostSecret = (gameId: string, secret: string) => {
        const normalizedGameId = normalizeGameId(gameId)
        hostSecrets.value = {
            ...hostSecrets.value,
            [normalizedGameId]: secret,
        }

        // Always persist to sessionStorage so the secret survives HMR restarts.
        // sessionStorage is tab-scoped and clears on tab close, so this is safe.
        if (import.meta.client) {
            sessionStorage.setItem(hostSecretStorageKey(normalizedGameId), secret)
        }
    }

    const clearHostSecret = (gameId: string) => {
        const normalizedGameId = normalizeGameId(gameId)
        hostSecrets.value = {
            ...hostSecrets.value,
            [normalizedGameId]: '',
        }

        if (import.meta.client) {
            sessionStorage.removeItem(hostSecretStorageKey(normalizedGameId))
        }
    }

    const clearHostClaimError = () => {
        hostClaimErrorCode.value = null
        hostClaimErrorMessage.value = null
    }

    const getHostSecret = (gameId: string): string => {
        const normalizedGameId = normalizeGameId(gameId)
        const fromMemory = hostSecrets.value[normalizedGameId]
        if (fromMemory) return fromMemory

        if (import.meta.client) {
            return sessionStorage.getItem(hostSecretStorageKey(normalizedGameId)) || ''
        }

        return ''
    }

    const getStoredPlayerProfile = (gameId?: string | null): StoredPlayerProfile => {
        if (import.meta.server || !gameId) {
            return { id: null, name: null, avatar: null }
        }

        const normalizedGameId = normalizeGameId(gameId)
        return {
            id: getItemWithTTL(playerStorageKey(normalizedGameId, 'id'), PER_GAME_SESSION_TTL_MS),
            name: getItemWithTTL(playerStorageKey(normalizedGameId, 'name'), PER_GAME_SESSION_TTL_MS),
            avatar: getItemWithTTL(playerStorageKey(normalizedGameId, 'avatar'), PER_GAME_SESSION_TTL_MS)
        }
    }

    const getLatestPlayerProfile = (): Omit<StoredPlayerProfile, 'id'> => {
        if (import.meta.server) {
            return { name: null, avatar: null }
        }

        try {
            const raw = localStorage.getItem(latestPlayerProfileStorageKey)
            if (!raw) return { name: null, avatar: null }

            const parsed = JSON.parse(raw) as Partial<StoredPlayerProfile>
            return {
                name: typeof parsed.name === 'string' ? parsed.name : null,
                avatar: typeof parsed.avatar === 'string' ? parsed.avatar : null,
            }
        } catch {
            localStorage.removeItem(latestPlayerProfileStorageKey)
            return { name: null, avatar: null }
        }
    }

    const storePendingSessionTransfer = (transfer: PendingSessionTransfer) => {
        if (import.meta.server) return
        sessionStorage.setItem(pendingSessionTransferStorageKey, JSON.stringify({
            ...transfer,
            sourceGameId: normalizeGameId(transfer.sourceGameId),
            targetGameId: normalizeGameId(transfer.targetGameId),
        }))
    }

    const consumePendingSessionTransfer = (targetGameId?: string | null): PendingSessionTransfer | null => {
        if (import.meta.server) return null

        const raw = sessionStorage.getItem(pendingSessionTransferStorageKey)
        if (!raw) return null

        try {
            const parsed = JSON.parse(raw) as Partial<PendingSessionTransfer>
            const normalizedTargetGameId = normalizeGameId(targetGameId || '')
            if (!parsed.targetGameId || normalizeGameId(parsed.targetGameId) !== normalizedTargetGameId) {
                return null
            }

            sessionStorage.removeItem(pendingSessionTransferStorageKey)
            return {
                sourceGameId: normalizeGameId(parsed.sourceGameId || ''),
                targetGameId: normalizeGameId(parsed.targetGameId),
                playerId: String(parsed.playerId || ''),
                name: String(parsed.name || ''),
                avatar: typeof parsed.avatar === 'string' ? parsed.avatar : null,
                startedAt: Number(parsed.startedAt) || Date.now(),
            }
        } catch {
            sessionStorage.removeItem(pendingSessionTransferStorageKey)
            return null
        }
    }

    const restoreSession = (gameId?: string) => {
        if (import.meta.server) return

        const targetGameId = normalizeGameId(gameId || localGameId.value || '')
        if (!targetGameId) return

        localGameId.value = targetGameId
        const { id: savedId, name: savedName } = getStoredPlayerProfile(targetGameId)

        if (savedId) {
            localPlayerId.value = savedId
            console.log('[useGameSession] Session restored:', { gameId: targetGameId, savedId, savedName })
            return
        }

        localPlayerId.value = null
    }

    const buildCreateGamePayload = (templateId?: string, referral?: CreateGameReferral) => {
        if (!referral) {
            return templateId ? { template: templateId } : {}
        }

        return {
            ...(templateId ? { template: templateId } : {}),
            referral,
        }
    }

    const createGame = async (templateId?: string, options?: { referral?: CreateGameReferral }): Promise<string> => {
        if (!socket.value) connect()

        const response = await emitWithAck<{ gameId: string, hostSecret: string, error?: string }, { template?: string, referral?: CreateGameReferral }>({
            getSocket: () => socket.value,
            event: 'create_game',
            payload: buildCreateGamePayload(templateId, options?.referral),
            waitTimeoutMs: 10000,
            ackTimeoutMs: 10000,
            pollIntervalMs: 100,
        })

        if (response.error) {
            console.error('[useGameSession] create_game failed:', response.error)
            throw new Error(response.error)
        }

        if (!response.gameId) {
            throw new Error('Invalid server response')
        }

        console.log('[useGameSession] Game Created:', response.gameId)
        localGameId.value = response.gameId
        isHost.value = true
        isHostAuthenticated.value = true
        storeHostSecret(response.gameId, response.hostSecret)
        if (options?.referral?.shareId) {
            clearReferralContext()
        }
        console.log('[useGameSession] 🔐 Host Secret Stored in runtime state')
        return response.gameId
    }

    const joinGame = (
        params: { gameId: string, name: string, existingId?: string, avatar?: string },
        onResult?: (result: JoinGameAck) => void
    ) => {
        const { gameId, name, existingId, avatar } = params
        if (!socket.value) connect()

        const normalizedGameId = normalizeGameId(gameId)
        const id = existingId || generateId()
        localPlayerId.value = id
        localGameId.value = normalizedGameId

        persistStoredPlayerProfile(normalizedGameId, { id, name, avatar: avatar || null })

        const player: Player = {
            id,
            name,
            avatar,
            score: 0,
            connected: true
        }

        void waitForSocket(() => socket.value, { timeoutMs: 5000, pollIntervalMs: 100 })
            .then((activeSocket) => {
                if (onResult) {
                    activeSocket.emit('join_game', { gameId: normalizedGameId, player }, (result: JoinGameAck) => {
                        if (!result.ok) {
                            clearStoredPlayerProfile(normalizedGameId)
                            localPlayerId.value = null
                        }
                        onResult(result)
                    })
                    return
                }
                activeSocket.emit('join_game', { gameId: normalizedGameId, player })
            })
            .catch(() => {
                if (!onResult) return
                clearStoredPlayerProfile(normalizedGameId)
                localPlayerId.value = null
                onResult({
                    ok: false,
                    code: 'JOIN_FAILED',
                    message: 'Unable to connect to server',
                })
            })
    }

    const checkState = async (gameId: string, updatedAt = 0): Promise<CheckStateResult> => {
        if (!socket.value) connect()

        try {
            return await emitWithAck<CheckStateResult>({
                getSocket: () => socket.value,
                event: 'check_state',
                payload: getCheckStatePayload(gameId, updatedAt),
                waitTimeoutMs: 5000,
                ackTimeoutMs: 5000,
                pollIntervalMs: 100,
            })
        } catch {
            return Promise.reject(new Error('State check timed out'))
        }
    }

    const emitCheckState = (gameId: string, updatedAt = 0) => {
        if (!socket.value) return
        socket.value.emit('check_state', getCheckStatePayload(gameId, updatedAt))
    }

    const emitJoinGame = (payload: { gameId: string, player: Player }, onResult?: (result: JoinGameAck) => void) => {
        if (!socket.value) return
        if (onResult) {
            socket.value.emit('join_game', payload, onResult)
            return
        }
        socket.value.emit('join_game', payload)
    }

    const emitClaimHost = (gameId: string, secret: string) => {
        if (!socket.value) return
        socket.value.emit('claim_host', { gameId, secret })
    }

    const emitCreateGame = async (templateId?: string, options?: { referral?: CreateGameReferral }) => {
        return await emitWithAck<{ gameId: string, hostSecret: string, error?: string }, { template?: string, referral?: CreateGameReferral }>({
            getSocket: () => socket.value,
            event: 'create_game',
            payload: buildCreateGamePayload(templateId, options?.referral),
            waitTimeoutMs: 10000,
            ackTimeoutMs: 10000,
            pollIntervalMs: 100,
        })
    }

    const claimHost = (gameId: string, secret?: string) => {
        if (!socket.value) connect()

        const normalizedGameId = normalizeGameId(gameId)
        const s = (secret || getHostSecret(normalizedGameId)).trim()

        if (!s) {
            console.warn(`[useGameSession] ⚠️ Cannot claim host: No secret found for ${normalizedGameId}`)
            isClaimingHost.value = false
            hostClaimErrorCode.value = 'MISSING_SECRET'
            hostClaimErrorMessage.value = 'No host secret was found on this device.'
            return
        }

        isClaimingHost.value = true
        hostClaimAttempts.value += 1
        hostClaimLastAttemptAt.value = Date.now()
        clearHostClaimError()
        console.log(`[useGameSession] 🔑 Attempting to claim Host authority for ${normalizedGameId}...`)
        emitClaimHost(normalizedGameId, s)
    }

    // Listen for host claim result
    // We can define this here or in sync. 
    // Since it updates session state (isHostAuthenticated), let's keep it here 
    // BUT we need to register the listener.
    // Ideally useGameSync handles ALL listeners to avoid multiple registration points, 
    // OR we register specific session listeners here.
    // Let's expose a setupSessionListeners function.

    const hostClaimedHandler = (data: HostClaimResult) => {
        console.log('[useGameSession] host_claimed:', data.success)
        isClaimingHost.value = false
        isHostAuthenticated.value = data.success
        if (data.success) {
            isHost.value = true
        } else {
            hostClaimErrorCode.value = data.reason
            hostClaimErrorMessage.value = data.message || 'Host claim failed'
        }
    }

    const setupSessionListeners = () => {
        if (!socket.value) return
        bindSocketListener(socket.value, 'host_claimed', hostClaimedHandler)
    }

    return {
        localPlayerId,
        localGameId,
        isHost,
        isHostAuthenticated,
        isClaimingHost,
        hostClaimAttempts,
        hostClaimErrorCode,
        hostClaimErrorMessage,
        hostClaimLastAttemptAt,
        isConnected,
        restoreSession,
        getStoredPlayerProfile,
        getLatestPlayerProfile,
        persistStoredPlayerProfile,
        storeHostSecret,
        storePendingSessionTransfer,
        consumePendingSessionTransfer,
        getHostSecret,
        clearHostSecret,
        clearHostClaimError,
        createGame,
        joinGame,
        checkState,
        emitCheckState,
        emitJoinGame,
        emitClaimHost,
        emitCreateGame,
        claimHost,
        setupSessionListeners,
        socket // Expose for other composables if needed
    }
}
